// src/components/Common/ErrorAlert.tsx

import React from 'react';
import { AlertCircle, Lock, Wifi, Clock, Info, RefreshCw } from 'lucide-react';
import { CategorizedError } from 'utils/helpers';

interface ErrorAlertProps {
    error: CategorizedError;
    onRetry?: () => void;
    className?: string;
}

/**
 * Reusable error alert component with categorized error display
 * Shows appropriate icon, color, and actionable guidance based on error type
 */
const ErrorAlert: React.FC<ErrorAlertProps> = ({ error, onRetry, className = '' }) => {
    // Determine colors based on severity
    const severityColors = {
        error: {
            bg: 'bg-red-50 dark:bg-red-900/20',
            border: 'border-red-200 dark:border-red-800',
            icon: 'text-red-600 dark:text-red-400',
            title: 'text-red-800 dark:text-red-300',
            message: 'text-red-700 dark:text-red-400',
            action: 'text-red-600 dark:text-red-400',
            button: 'bg-red-600 hover:bg-red-700 text-white'
        },
        warning: {
            bg: 'bg-yellow-50 dark:bg-yellow-900/20',
            border: 'border-yellow-200 dark:border-yellow-800',
            icon: 'text-yellow-600 dark:text-yellow-400',
            title: 'text-yellow-800 dark:text-yellow-300',
            message: 'text-yellow-700 dark:text-yellow-400',
            action: 'text-yellow-600 dark:text-yellow-400',
            button: 'bg-yellow-600 hover:bg-yellow-700 text-white'
        }
    };

    const colors = severityColors[error.severity];

    // Select icon based on error type
    const getIcon = () => {
        const iconClass = `w-5 h-5 ${colors.icon}`;
        switch (error.icon) {
            case 'lock':
                return <Lock className={iconClass} />;
            case 'wifi':
                return <Wifi className={iconClass} />;
            case 'clock':
                return <Clock className={iconClass} />;
            case 'info':
                return <Info className={iconClass} />;
            case 'alert':
            default:
                return <AlertCircle className={iconClass} />;
        }
    };

    return (
        <div className={`${colors.bg} border ${colors.border} rounded-lg p-4 ${className}`}>
            <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                    {getIcon()}
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className={`text-sm font-medium ${colors.title} mb-1`}>
                        {error.title}
                    </h3>
                    <p className={`text-sm ${colors.message} mb-2`}>
                        {error.message}
                    </p>
                    <p className={`text-sm ${colors.action}`}>
                        {error.action}
                    </p>
                </div>
                {onRetry && (error.type === 'network' || error.type === 'timeout' || error.type === 'generic') && (
                    <div className="flex-shrink-0">
                        <button
                            onClick={onRetry}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md ${colors.button} transition-colors`}
                        >
                            <RefreshCw className="w-4 h-4" />
                            Retry
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ErrorAlert;
